<template>
    <div>
        <div class="mb-3 flex justify-end gap-x-5">
            <hts-form-button
                v-if="checkPermission(layoutPermissions.adminCP.node_type_add)"
                color="main"
                icon-name="hi-plus-circle-icon"
                icon-position="right"
                type="submit"
                @click="addNodeType.open()">
                {{ $t('labels.nodeTypeModule.add') }}
            </hts-form-button>
        </div>

        <hts-data-table
            ref="tables"
            :headers="headers"
            view-deleted
            :server="apiGetNodeType"
            :auto-size="autoSizeTable"
            :use-archive="useNodeTypeArchive"
            :use-restore="useNodeTypeRestore"
            use-selection
            store-name="nodeDataTable"
            store-key="NodeType" />

        <add-node-type
            ref="addNodeType"
            @refresh-tables="refreshTables()" />

        <edit-node-type
            ref="editNodeType"
            @refresh-tables="refreshTables()" />

        <detail-node-type ref="detailNodeType" />

        <delete-node-type
            :show="deleteNodeType"
            :node-type-id="nodeTypeIdSelectedForDelete"
            @refresh-tables="refreshTables()"
            @close="deleteNodeType = false" />
    </div>
</template>
<script setup lang="ts">
import { INodeType } from './ignore/imports/model'
import BusinessNameCell from './ignore/tables/business-name.vue'
import AddNodeType from './ignore/type/add.vue'
import DeleteNodeType from './ignore/type/delete.vue'
import DetailNodeType from './ignore/type/detail.vue'
import EditNodeType from './ignore/type/edit.vue'
import layoutPermissions from '~/data/permissions/layout-permissions'
const apiGetNodeType = ref({ ...$urlAdmin.node.type.getList })
const { $formatters } = useNuxtApp()
const nodeTypeMultiSelected = ref<Array<INodeType> | null>()
const viewNodeTypeArchive = ref(false)

const tables = ref()
const addNodeType = ref()
const editNodeType = ref()
const detailNodeType = ref()
const deleteNodeType = ref(false)
const nodeTypeSelected = ref<INodeType | undefined>(undefined)
const nodeTypeIdSelectedForDelete = ref<{ [key: string]: any } | undefined>(undefined)

const handleEdit = (nodeType: INodeType) => {
  nodeTypeSelected.value = nodeType
  editNodeType.value.open(nodeType._id)
}

const handleDetail = (nodeType: INodeType) => {
  nodeTypeSelected.value = nodeType
  detailNodeType.value.open(nodeType._id)
}

const handleDelete = (nodeType: { [key: string]: any }) => {
  if (nodeType && nodeType._id) {
    nodeTypeIdSelectedForDelete.value = {
      selectedItems: [nodeType._id]
    }
  } else {
    nodeTypeIdSelectedForDelete.value = nodeType
  }
  deleteNodeType.value = true
}

const handleViewNodeArchive = (action: boolean) => {
  viewNodeTypeArchive.value = action
  apiGetNodeType.value.method = $urlAdmin.node.type.getList.method
  apiGetNodeType.value.url = $urlAdmin.node.type.getList.url.replace('ARCHIVE', String(action))
  nextTick(() => {
    if (tables.value) {
      tables.value.refresh()
    }
  })
}

const headers = computed(() => [
  {
    field: 'name',
    sortable: true,
    headerName: $t('labels.nodeTypeName'),
    filter: 'agTextColumnFilter'
  },
  {
    field: 'businesses',
    headerName: $t('labels.business'),
    cellRenderer: BusinessNameCell,
    autoHeight: true,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'description',
    sortable: true,
    headerName: $t('labels.description')
  },
  {
    field: 'createdAt',
    headerName: $t('labels.createdAt'),
    sortable: true,
    valueGetter: (params: any) => {
      if (params.data.createdAt) {
        return $formatters.datetime(params.data.createdAt)
      }
    }
  },
  {
    field: 'updatedAt',
    headerName: $t('labels.updatedAt'),
    sortable: true,
    valueGetter: (params: any) => {
      if (params.data.updatedAt) {
        return $formatters.datetime(params.data.updatedAt)
      }
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    popup: true,
    suppressRowTransform: true,
    suppressMenu: true,
    resizable: false,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: 'Actions',
    cellRendererParams: {
      storeKey: 'nodeDataTable',
      storeName: 'NodeType',
      items: [
        {
          icon: 'hi-eye-icon',
          label: $t('labels.viewDetail'),
          class: 'text-blue-600 hover:text-blue-500',
          isAll: true,
          click: handleDetail
        },
        {
          icon: 'hi-pencil-square-icon',
          label: $t('labels.edit'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.adminCP.node_type_edit),
          click: handleEdit
        },
        {
          icon: 'hi-trash-icon',
          label: $t('labels.delete'),
          class: 'text-error-600 hover:text-error-500',
          permissions: checkPermission(layoutPermissions.adminCP.node_type_delete),
          isArchive: true,
          click: handleDelete
        }
      ]
    }
  }
])

const refreshTables = () => {
  if (tables.value) {
    tables.value.refresh()
  }
  deleteNodeType.value = false
  nodeTypeIdSelectedForDelete.value = {}
}

const autoSizeTable = {
  active: false,
  skipHeader: false
}

const useNodeTypeArchive = computed(() => {
  return { api: $urlAdmin.node.type.archive, permissions: checkPermission(layoutPermissions.adminCP.node_type_archive) }
})
const useNodeTypeRestore = computed(() => {
  return { api: $urlAdmin.node.type.restore, permissions: checkPermission(layoutPermissions.adminCP.node_type_archive) }
})

const selectionActions = computed(() => {
  const action = []
  action.push({ title: $t('labels.delete'), fn: handleDelete, class: 'text-error-600', isArchive: true })
  return action
})
</script>
